import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem("company_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      return;
    }

    const formData = new FormData(form);
    const submitAddr = form.getAttribute("action") || "";

    try {
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries()).filter(
            (entry): entry is [string, string] =>
              typeof entry[1] === "string" && entry[0] !== "company_alt"
          )
        ).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || responseText || "";

      if (
        response.ok &&
        parsed?.code === "OK"
      ) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
        setFormError(serverMsg || t("about.contact.error"));
      }
    } catch {
      setFormStatus("error");
      setFormError(t("about.contact.error"));
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: t("about.title") }]} />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-4">
              {t("about.title")}
            </h1>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-5">
                  {t("about.story")}
                </h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-3">
                  <p>
                    久久插成立于 2008 年，十八年来始终专注于插接技术的研发与创新。从最初的工业连接器起步，久久插逐步拓展至家用装配、专业施工、精密仪器等领域，成为国内领先的持久插接解决方案提供商。
                  </p>
                  <p>
                    久久插的品牌理念源自对「连接」二字的深刻理解——连接不仅仅是电气导通，更是安全、可靠与持久。每一件久久插产品都承载着对品质的执着追求。
                  </p>
                  <p>
                    截至目前，久久插已服务超过 10,000
                    家企业客户，产品远销 30
                    多个国家和地区，在新能源、智能制造、轨道交通等领域建立了卓越口碑。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20clean%20manufacturing%20facility%20with%20organized%20assembly%20lines%20for%20electrical%20connectors%2C%20bright%20natural%20lighting%2C%20workers%20in%20clean%20uniforms%2C%20professional%20industrial%20photography%2C%20warm%20amber%20and%20white%20tones%2C%20high%20quality%20corporate%20image&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="久久插制造车间"
                  title="久久插——十八年专注插接技术"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-10 text-center">
              {t("about.values")}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  icon: "ri-verified-badge-line",
                  value: "CCC/CE/UL",
                  label: t("about.values.cert"),
                  desc: t("about.values.cert.desc"),
                  color: "accent",
                },
                {
                  icon: "ri-building-2-line",
                  value: "10,000+",
                  label: t("about.values.clients"),
                  desc: t("about.values.clients.desc"),
                  color: "primary",
                },
                {
                  icon: "ri-history-line",
                  value: "18 年",
                  label: t("about.values.years"),
                  desc: t("about.values.years.desc"),
                  color: "secondary",
                },
                {
                  icon: "ri-lightbulb-line",
                  value: "50+",
                  label: t("about.values.patents"),
                  desc: t("about.values.patents.desc"),
                  color: "accent",
                },
              ].map((stat, idx) => {
                const colorMap: Record<string, string> = {
                  primary: "bg-primary-100 text-primary-700",
                  accent: "bg-accent-100 text-accent-700",
                  secondary: "bg-secondary-100 text-secondary-700",
                };
                return (
                  <div
                    key={idx}
                    className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div
                      className={`w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center ${colorMap[stat.color]}`}
                    >
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">
                      {stat.label}
                    </div>
                    <p className="text-xs text-foreground-500">{stat.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 bg-background-50" id="contact">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-10 text-center">
              {t("about.contact")}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      {t("about.contact.address")}
                    </h3>
                    <p className="text-sm text-foreground-600">
                      上海市浦东新区张江高科技园区博云路 88 号久久插大厦
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      {t("about.contact.phone")}
                    </h3>
                    <p className="text-sm text-foreground-600">
                      400-888-6688
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      {t("about.contact.email")}
                    </h3>
                    <p className="text-sm text-foreground-600">
                      service@jiujiucha.com
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      {t("about.contact.hours")}
                    </h3>
                    <p className="text-sm text-foreground-600">
                      周一至周五 9:00-18:00
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <h3 className="text-sm font-semibold text-foreground-900 mb-5">
                  {t("about.contact.form")}
                </h3>

                {formStatus === "success" ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-800">
                      {t("about.contact.success")}
                    </p>
                  </div>
                ) : (
                  <form
                    id="about-contact-form"
                    data-readdy-form="true"
                    onSubmit={handleSubmit}
                    action="https://readdy.ai/api/form/d9rdi7nnahb7382ovl0g"
                    method="POST"
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-hp"
                    />
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-xs font-medium text-foreground-700 mb-1.5"
                      >
                        {t("about.contact.name")}
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder={t("about.contact.name.placeholder")}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-xs font-medium text-foreground-700 mb-1.5"
                      >
                        {t("about.contact.email")}
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder={t("about.contact.email.placeholder")}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-phone"
                        className="block text-xs font-medium text-foreground-700 mb-1.5"
                      >
                        {t("about.contact.phone")}
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        placeholder={t("about.contact.phone.placeholder")}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-xs font-medium text-foreground-700 mb-1.5"
                      >
                        {t("about.contact.message")}
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        placeholder={t("about.contact.message.placeholder")}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">
                        {charCount}/500
                      </p>
                    </div>

                    {formStatus === "error" && (
                      <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      {t("about.contact.submit")}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Organization Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "久久插",
              url: "https://www.jiujiucha.com/",
              description:
                "久久插，专注于持久稳固的插接解决方案，提供工业级插接器、家用插接配件、施工专用插接装置等产品。",
              address: {
                "@type": "PostalAddress",
                streetAddress: "张江高科技园区博云路 88 号久久插大厦",
                addressLocality: "上海",
                addressRegion: "浦东新区",
                addressCountry: "CN",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+86-400-888-6688",
                contactType: "customer service",
                availableLanguage: ["Chinese"],
              },
              sameAs: [],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "AboutPage",
              name: "关于久久插",
              description:
                "久久插成立于2008年，十八年专注于插接技术的研发与创新。",
              url: "https://www.jiujiucha.com/about",
              breadcrumb: {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: "https://www.jiujiucha.com/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "关于我们",
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}